/**
 * Route-matching helpers for "which nav entry is active?".
 *
 * Two things bite the naive `pathname === url` check, and both reached the
 * sidebar through ordinary shared links:
 *
 *  1. A TRAILING SLASH. React Router matches `/event/x/guests/guestlist/`
 *     against the `.../guestlist` route just fine, but hands the slash through
 *     in `useLocation().pathname` verbatim. Exact-equality then failed and the
 *     Guests submenu stayed closed while only the parent row lit up (the
 *     prefix check `startsWith` still passed).
 *  2. PREFIX BLEED. `startsWith(base)` alone would light `/event/x/app` up for
 *     a future `/event/x/apps`; matching must stop at a segment boundary.
 *
 * Every nav surface (event sidebar, user sidebar, template sidebar) goes
 * through here so the three copies cannot drift again.
 */

/**
 * Canonical form of a location pathname: no query/hash (defensive — React
 * Router already splits them off), no trailing slash except for the root.
 */
export function normalizePathname(
  pathname: string | null | undefined,
): string {
  if (!pathname) return "";
  const withoutQuery = pathname.split("?")[0].split("#")[0];
  const trimmed = withoutQuery.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

/** True when `pathname` IS `base` or sits below it on a segment boundary. */
export function isPathActive(
  pathname: string | null | undefined,
  base: string,
): boolean {
  const current = normalizePathname(pathname);
  const target = normalizePathname(base);
  return current === target || current.startsWith(`${target}/`);
}

/** True when `pathname` is exactly `target`, trailing slash notwithstanding. */
export function isSamePath(
  pathname: string | null | undefined,
  target: string,
): boolean {
  return normalizePathname(pathname) === normalizePathname(target);
}
